import { sesUrl } from '@/config/app.config';
import { get, post, qs } from '@/results/results.api';

/**
 * GS-904 — commission rules on the result site (MATCH-ODDS-ENGINE.md §6.7; SES `commission/`, GS-836's API). Rules are
 * append-only: saving replaces the current rule with the same level, owner, product and sport; closing ends it ("not
 * configured" from now — the search falls through to the level above), while a rule that charges nothing is a real
 * answer that stops the search. A player's rate is found from the player up: their own rule, each agent above them
 * (nearest first), their site, then global; nothing found means no commission.
 */
export type Scope = 'USER' | 'UPLINE' | 'WHITELABEL' | 'GLOBAL';
export type RuleProduct = 'ALL' | 'MATCH_ODDS' | 'BOOKMAKER' | 'FANCY' | 'PREMIUM';
export type Payee = 'DIRECT_MANAGER' | 'RULE_OWNER' | 'HOUSE';

export interface CommissionRule {
  id: number;
  scope: Scope;
  scopeRef: number | null;
  /** The player's or agent's name (USER / UPLINE). */
  username: string | null;
  /** The site's name (WHITELABEL). */
  siteName: string | null;
  product: RuleProduct;
  sportId: number | null;
  enabled: boolean;
  ratePct: string;
  payee: Payee;
  downlineCapPct: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  current: boolean;
  createdBy: number;
  createdByName: string | null;
  createdAt: string;
  supersededBy: number | null;
  note: string | null;
}

/** What the form saves (SES CommissionRuleRequest). */
export interface RuleDraft {
  scope: Scope;
  scopeRef?: number;
  product: RuleProduct;
  sportId?: number;
  enabled: boolean;
  ratePct?: string;
  payee: Payee;
  downlineCapPct?: string;
  note?: string;
}

/** A user of the signed-in agent's tree, found by name. */
export interface FoundUser {
  id: number;
  username: string;
  kind: 'PLAYER' | 'AGENT';
  manager: string | null;
}

export interface Site {
  id: number;
  name: string;
  dns: string | null;
}

/** What a player pays now for a product and sport: the settlement's own resolver (gs-bet-core) over the same rules. */
export interface Effective {
  playerId: number;
  product: RuleProduct;
  sportId: number | null;
  charged: boolean;
  ratePct: string;
  payeeId: number | null;
  rule: CommissionRule | null;
}

const C = '/api/director/v2/commission-rules';

export async function loadRules(history: boolean): Promise<{ rules: CommissionRule[]; admin: boolean }> {
  const data = await get<{ rules?: CommissionRule[]; admin?: boolean }>(sesUrl(C + '?' + qs({ history: history ? 'true' : undefined,
    limit: 1000 })));
  return { rules: data.rules || [], admin: !!data.admin };
}

export async function saveRule(d: RuleDraft): Promise<CommissionRule> {
  return (await post<{ rule: CommissionRule }>(sesUrl(C), d)).rule;
}

export async function closeRule(id: number, reason: string): Promise<CommissionRule> {
  return (await post<{ rule: CommissionRule }>(sesUrl(C + '/' + id + '/close'), { reason })).rule;
}

export async function lookupUser(username: string): Promise<FoundUser> {
  return (await get<{ user: FoundUser }>(sesUrl(C + '/lookup?' + qs({ username })))).user;
}

export async function loadSites(): Promise<Site[]> {
  return (await get<{ sites?: Site[] }>(sesUrl(C + '/sites'))).sites || [];
}

export async function effectiveRate(userId: number, product: RuleProduct, sportId?: number): Promise<Effective> {
  return (await get<{ effective: Effective }>(sesUrl(C + '/effective?' + qs({ userId, product, sportId })))).effective;
}

// ------------------------------------------------------------------------------------------------ in words

export const PRODUCTS: Array<{ key: RuleProduct; label: string }> = [
  { key: 'MATCH_ODDS', label: 'Match Odds' },
  { key: 'BOOKMAKER', label: 'Bookmaker' },
  { key: 'FANCY', label: 'Fancy' },
  { key: 'PREMIUM', label: 'Premium' },
  { key: 'ALL', label: 'All products' },
];

/** The feed's sport ids (Match.sportsId = SES sportId). */
export const SPORTS: Array<{ id: number; label: string }> = [
  { id: 1, label: 'Cricket' }, { id: 2, label: 'Soccer' }, { id: 3, label: 'Tennis' }, { id: 4, label: 'E-Soccer' },
];

export const SCOPES: Array<{ key: Scope; label: string; admin: boolean }> = [
  { key: 'USER', label: 'Player', admin: false },
  { key: 'UPLINE', label: 'Agent', admin: false },
  { key: 'WHITELABEL', label: 'Site', admin: true },
  { key: 'GLOBAL', label: 'Global', admin: true },
];

/** The products an ALL rule never reaches (gs-bet-core Product.coveredByAll: BM-D1, FN-D1, SB-D…). */
export const NOT_COVERED_BY_ALL: RuleProduct[] = ['BOOKMAKER', 'FANCY', 'PREMIUM'];

export function productName(p: RuleProduct): string {
  const found = PRODUCTS.find((x) => x.key === p);
  return found ? found.label : p;
}

export function sportName(id: number | null | undefined): string {
  if (id === null || id === undefined) {
    return 'every sport';
  }
  const found = SPORTS.find((s) => s.id === id);
  return found ? found.label : 'sport ' + id;
}

export function scopeName(s: Scope): string {
  const found = SCOPES.find((x) => x.key === s);
  return found ? found.label : s;
}

/** Whose rule it is: the player, the agent, the site, or everyone. */
export function ruleWho(r: { scope: Scope; scopeRef?: number | null; username?: string | null; siteName?: string | null }): string {
  switch (r.scope) {
    case 'GLOBAL':
      return 'Everyone';
    case 'WHITELABEL':
      return r.siteName || 'Site ' + r.scopeRef;
    default:
      return r.username || 'User ' + r.scopeRef;
  }
}

/** Who receives it, in words. */
export function payeeWords(payee: Payee, owner: string): string {
  switch (payee) {
    case 'RULE_OWNER':
      return 'paid to ' + owner;
    case 'HOUSE':
      return 'kept by the house';
    default:
      return "paid to the player's direct manager";
  }
}

/**
 * The rule as one sentence, the way an operator checks it before saving: who pays, what, on which product and sport,
 * to whom, and the cap it puts on the levels below.
 */
export function describeRule(r: { scope: Scope; enabled: boolean; ratePct?: string | null; product: RuleProduct; sportId?: number | null;
  payee: Payee; downlineCapPct?: string | null }, who: string): string {
  const players = r.scope === 'USER' ? who : r.scope === 'UPLINE' ? 'Every player under ' + who
    : r.scope === 'WHITELABEL' ? 'Every player of ' + who : 'Every player';
  const what = productName(r.product) + ', ' + sportName(r.sportId);
  let text = r.enabled
    ? `${players} pays ${Number(r.ratePct || 0).toFixed(2)}% of their profit on each market (${what}), ${payeeWords(r.payee, who)}.`
    : `${players} pays no commission (${what}) — this stops the search here.`;
  if (r.scope !== 'USER') {
    text += ' A rule nearer the player wins over this one.';
  }
  if (r.product === 'ALL') {
    text += ' All covers Match Odds only: bookmaker, fancy and premium need their own rule.';
  }
  if (r.scope === 'UPLINE' && r.downlineCapPct) {
    text += ` No rule below ${who} may charge more than ${Number(r.downlineCapPct).toFixed(2)}%.`;
  }
  return text;
}

/** A rate the API accepts: 0.00 to 25.00, at most 2 decimals ('' when it is fine). */
export function rateProblem(value: string, name = 'The rate'): string {
  const v = (value || '').trim();
  if (!/^\d{1,2}(\.\d{1,2})?$/.test(v)) {
    return `${name} is a number like 2 or 1.50`;
  }
  const n = Number(v);
  return n < 0 || n > 25 ? `${name} is 0 to 25%` : '';
}
